import type { Vec2 } from './types/vec2'
import type { RotatedRectGeometry } from './types/rotated-rect-geometry'

export function normalizeRotationDegrees(d: number): number {
  if (!Number.isFinite(d)) return 0
  return ((d % 360) + 360) % 360
}

/** Shift-constrained canvas rotation: snap to this increment in degrees. */
export const ROTATION_SHIFT_SNAP_INCREMENT = 15

/** Hard snap to the nearest `increment` degree tick (e.g. 15° while Shift is held). */
export function snapRotationToIncrement(
  degrees: number,
  increment = ROTATION_SHIFT_SNAP_INCREMENT
): number {
  const normalized = normalizeRotationDegrees(degrees)

  return normalizeRotationDegrees(Math.round(normalized / increment) * increment)
}

/** Smallest signed delta from `from` to `to` in (-180, 180]. */
export function shortestDeltaDegrees(from: number, to: number): number {
  const a = normalizeRotationDegrees(from)
  const b = normalizeRotationDegrees(to)
  let d = b - a

  if (d > 180) d -= 360
  if (d < -180) d += 360
  return d
}

export function rotateVector(vec: Vec2, degrees: number): Vec2 {
  const rad = (degrees * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)

  return {
    x: vec.x * cos - vec.y * sin,
    y: vec.x * sin + vec.y * cos,
  }
}

export function localVecToCanvas(vec: Vec2, rotationDeg: number): Vec2 {
  const rad = (rotationDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)

  return { x: vec.x * cos - vec.y * sin, y: vec.x * sin + vec.y * cos }
}

export function canvasVecToLocal(vec: Vec2, rotationDeg: number): Vec2 {
  const rad = (rotationDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)

  return { x: vec.x * cos + vec.y * sin, y: -vec.x * sin + vec.y * cos }
}

export function boxVerticesForShapeGeometry(geom: RotatedRectGeometry): Vec2[] {
  const cx = geom.x + geom.width / 2
  const cy = geom.y + geom.height / 2
  const hw = geom.width / 2
  const hh = geom.height / 2
  const corners = [
    { x: -hw, y: -hh },
    { x: hw, y: -hh },
    { x: hw, y: hh },
    { x: -hw, y: hh },
  ]

  return corners.map((corner) => {
    const rotated = localVecToCanvas(corner, geom.rotation)

    return {
      x: cx + rotated.x,
      y: cy + rotated.y,
    }
  })
}
